import { styled } from "@mui/material/styles";
import { Box, BoxProps } from "@mui/material";
// This shell exists only for the public pages, so it reads their surface helper directly rather than
// duplicating the canvas derivation.
import { publicPageCanvas } from "../../views/publicPages/publicPageSurfaces";

interface StyledBoxProps extends BoxProps {
    isSubdomain?: boolean;
}

const PREFIX = "GenericPublicPageWrapper";

export const classes = {
    pageWrapper: `${PREFIX}-pageWrapper`,
    rightSidePageWrapper: `${PREFIX}-rightSidePageWrapper`,
    pageContent: `${PREFIX}-pageContent`,
};

/**
 * Shell for the public (unauthenticated) pages.
 *
 * NG-8572 removed the left branding column from every public page — the branded entry point moved
 * to the separately hosted marketing site — so this is a single full-width content column. Pages
 * cap their own form measure via publicFormLayout; `isSubdomain` only shortens the viewport to make
 * room for the "Powered by 1Health" strip.
 */
export const StyledBox = styled(Box, { shouldForwardProp: (prop) => prop !== "isSubdomain" })<StyledBoxProps>(({ theme, isSubdomain }) => ({
    [`&.${classes.pageWrapper}`]: {
        display: "flex",
        flexWrap: "wrap",
        width: "100%",
        // minHeight, not height: putting the long registration forms inside a padded card pushes them
        // past the viewport, and a fixed height would clip the overflow instead of letting the page
        // scroll. The branding preview still pins this to 100% with !important, so it is unaffected.
        minHeight: isSubdomain ? "95vh" : "100vh",
    },

    [`& .${classes.rightSidePageWrapper}`]: {
        display: "flex",
        width: "100%",
        minHeight: isSubdomain ? "95vh" : "100vh",
        alignItems: "center",
        justifyContent: "center",
        // NG-8606: the canvas behind the card. Still branding-agnostic — publicPageCanvas keeps the
        // branded formBackground wherever it can, and only derives a tint when background.default and
        // background.paper collapse to the same colour (light mode, and every branding config), which
        // would otherwise leave the card invisible against the page.
        background: publicPageCanvas(theme),
        "@media(max-width:959px)": {
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "initial",
        },
    },

    // The page-entrance transition's element. It exists only so the glide has something to move
    // that is not the column: the column paints the canvas, so transforming or fading it would
    // slide the background off its own edge and expose the body behind it. These rules restate the
    // horizontal centring the column already does, which is what keeps the card's geometry
    // identical to when it was the column's direct child — verified at desktop and at the 959px
    // breakpoint, where the column flips to `flex-direction: column`.
    [`& .${classes.pageContent}`]: {
        display: "flex",
        justifyContent: "center",
        width: "100%",
    },
}));
